import { JobStatus, PartFinderReport } from '@/api/generatedApi';
import { PART_FINDER_ACTIVITY_QUERY_KEY } from '@/hooks/api/part-finder-activity/query';
import { PART_FINDER_REPORTS_QUERY_KEY } from '@/hooks/api/part-finder-reports/query';
import { useGetPartFinderReport } from '@/hooks/api/part-finder-reports/useGetPartFinderReport';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

const PART_FINDER_REPORT_POLL_INTERVAL_MS = 3000;

const isInProgress = (status: JobStatus | undefined): boolean =>
  status === JobStatus.PENDING || status === JobStatus.RUNNING;

export const usePartFinderReportPolling = (row: PartFinderReport): PartFinderReport => {
  const queryClient = useQueryClient();
  const wasInProgressReference = useRef(isInProgress(row.status));
  const lastFetchedReference = useRef<PartFinderReport | undefined>(undefined);

  const { data: fetchedReport } = useGetPartFinderReport(wasInProgressReference.current ? row.id : '', {
    refetchInterval: query =>
      isInProgress(query.state.data?.status ?? row.status) ? PART_FINDER_REPORT_POLL_INTERVAL_MS : false,
  });

  if (fetchedReport) {
    lastFetchedReference.current = fetchedReport;
  }

  useEffect(() => {
    if (fetchedReport && wasInProgressReference.current && !isInProgress(fetchedReport.status)) {
      wasInProgressReference.current = false;
      queryClient.invalidateQueries({ queryKey: [PART_FINDER_REPORTS_QUERY_KEY], exact: false });
      queryClient.invalidateQueries({ queryKey: [PART_FINDER_ACTIVITY_QUERY_KEY], exact: false });
    }
  }, [fetchedReport, queryClient]);

  return fetchedReport ?? lastFetchedReference.current ?? row;
};
